import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
  // scmAuthApiRef,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  alertApiRef,
  analyticsApiRef,
  configApiRef,
  createApiFactory,
  identityApiRef,
  // githubAuthApiRef,
} from '@backstage/core-plugin-api';
import { m42EnterpriseAuthApiFactory } from './components/auth/m42EnterpriseAuth';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import { GoogleAnalytics4 } from '@backstage-community/plugin-analytics-module-ga4';

// @backstage/plugin-notifications consumes toastApiRef from the new frontend
// system, which is not wired by @backstage/app-defaults. Forward toast posts
// to the legacy alertApi so the sidebar item and pages can render in this app.
// Caveats vs the real ToastApi: links are dropped (alertApi has no slot for
// them), and the returned close() is a no-op (alertApi exposes no handle).
// Both are acceptable here because the bell icon + /notifications page are the
// durable surfaces; the toast is just a transient echo.
const toastToAlertAdapter = createApiFactory({
  api: toastApiRef,
  deps: { alertApi: alertApiRef },
  factory: ({ alertApi }) => ({
    post(toast) {
      const parts = [toast.title, toast.description].filter(Boolean);
      const message = parts.map(String).join(': ');
      const severity =
        toast.status === 'danger'
          ? 'error'
          : toast.status === 'neutral'
          ? 'info'
          : toast.status ?? 'success';
      // Default to transient so toasts auto-dismiss, matching upstream ToastApi
      // behavior. Only stick on danger or when the caller explicitly asks for
      // a permanent toast by passing timeout: Infinity (rare).
      const display =
        toast.timeout === Infinity || severity === 'error'
          ? 'permanent'
          : 'transient';
      alertApi.post({ message, severity, display });
      return { close: () => {} };
    },
  }),
});

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  // createApiFactory({
  //   api: scmAuthApiRef,
  //   deps: {
  //       githubAuthApiRef: githubAuthApiRef,
  //   },
  //   factory: ({ githubAuthApiRef }) => ScmAuth.forGitlab(githubAuthApiRef),
  // }),

  ScmAuth.createDefaultApiFactory(),
  toastToAlertAdapter,
  m42EnterpriseAuthApiFactory,

  // Analytics — stock GA4 module (@backstage-community/plugin-analytics-module-ga4).
  // Registering analyticsApiRef makes Backstage auto-capture route pageviews
  // and lets components emit custom events via useAnalytics().captureEvent.
  //
  // Dormant until a measurement ID is configured: GoogleAnalytics4.fromConfig
  // THROWS when app.analytics.ga4.measurementId is absent, so we read it
  // optionally and fall back to a no-op AnalyticsApi when unset. This lets the
  // instrumentation ship now and "turn on" the day a GA4 property exists.
  //
  // To enable, add to the prod appConfig overrides
  // (gitops/argocd/env/infrastructure-prod/values.yaml) -- or
  // app-config.local.yaml for local testing:
  //
  //   app:
  //     analytics:
  //       ga4:
  //         measurementId: G-XXXXXXXXXX   # the GA4 property's measurement ID
  //
  // Events then flow with no code change. identity is left at the GA4 default
  // ('disabled'), so no user identity is sent unless app.analytics.ga4.identity
  // is set to 'optional'/'required'.
  createApiFactory({
    api: analyticsApiRef,
    deps: { configApi: configApiRef, identityApi: identityApiRef },
    factory: ({ configApi, identityApi }) => {
      const measurementId = configApi.getOptionalString(
        'app.analytics.ga4.measurementId',
      );
      if (!measurementId) {
        return { captureEvent: () => {} };
      }
      return GoogleAnalytics4.fromConfig(configApi, { identityApi });
    },
  }),
];
